/**
 * Simülasyon ekranının veri katmanı.
 *
 * Tüm uçlar Basic auth ister; yazma isteklerine `?username=` eklenir.
 * Beta ortamında uçların başına `/beta` gelir — proje genelindeki kural
 * "/simulasyon yolundaysan beta" olduğu için aynı kontrol burada da yapılır.
 */
import axios from "axios";
import { apiUrl } from "../../config";
import i18n from "../../i18next";

/** Beta ortamı öneki. Proje genelindeki kuralla aynı. */
const onek = () => (window.location.pathname === "/simulasyon" ? "/beta" : "");

const auth = {
  username: process.env.REACT_APP_USERNAME,
  password: process.env.REACT_APP_SECRET_KEY,
};

const yol = (p) => `${apiUrl}${onek()}${p}`;

/** Yazma isteklerinde kullanıcıyı taşıyan sorgu parametresi. */
const kullaniciyla = (p, kullanici) =>
  `${yol(p)}?username=${encodeURIComponent(kullanici ?? "")}`;

/**
 * Model koşusu uzun sürebilir. Backend sınırı 300 sn; istemci de aynı sınırı
 * kullanır ki kullanıcı yanıt gelmeden "hata" görmesin.
 */
const KOSU_TIMEOUT_MS = 300000;

/**
 * Sunucudan gelen hatayı kullanıcıya gösterilecek metne çevirir.
 *
 * Model planı reddettiğinde 400 + `{ "error": "..." }` ile Türkçe mesaj
 * dönüyor ve backend bunu aynen iletiyor — mesaj doğrudan gösterilir.
 * Model servisine ulaşılamadığında 502 dönüyor; o durumda kullanıcıya
 * teknik ayrıntı değil ne yapacağı söylenir.
 *
 * Modelin kendi mesajı (`error`) sunucudan Türkçe geliyor ve olduğu gibi
 * gösterilir — çevrilecek bir metin değil, modelin cevabı.
 */
export function hataMesaji(err, varsayilan = i18n.t("simulation.err.generic")) {
  const durum = err?.response?.status;
  if (durum === 502) return i18n.t("simulation.err.unreachable");
  if (err?.code === "ECONNABORTED") return i18n.t("simulation.err.timeout");
  const govde = err?.response?.data;
  if (typeof govde === "string" && govde.trim()) return govde;
  if (govde?.error) return govde.error;
  if (govde?.detail) return govde.detail;
  return err?.message ? `${varsayilan} (${err.message})` : varsayilan;
}

const al = async (p) => (await axios.get(yol(p), { auth })).data;

/* --- Sekme açılışı --------------------------------------------------- */

/** Son başlatılan order'ın plan satırları. */
export const planSatirlari = () => al("/getBSHStartStopResults");

/** Kaynak order_batch_id — uç düz sayı döndürür (örn. 9829). */
export const sonOrder = () => al("/getLatestOrder");

/* --- Malzeme listeleri ------------------------------------------------ */
// Malzeme kodu elle yazdırılmaz, bu listelerden seçtirilir: model bilinmeyen
// bir kodu reddetmiyor, sessizce kabul ediyor ve o satır stok/talep hesabına
// girmediği için sonuç yanıltıcı oluyor.

export const govdeMalzemeleri = () => al("/getBodyMaterial/all");
export const govdeOnMalzemeleri = () => al("/getFrontBodyMaterial/all");

/* --- Taslaklar -------------------------------------------------------- */

export const taslaklar = () => al("/getSimulationDraft/all");
export const taslak = (id) => al(`/getSimulationDraft/${id}`);

export const taslakEkle = async (govde, kullanici) =>
  (await axios.post(kullaniciyla("/addSimulationDraft", kullanici), govde, { auth }))
    .data;

export const taslakGuncelle = async (govde, kullanici) =>
  (await axios.put(kullaniciyla("/updateSimulationDraft", kullanici), govde, { auth }))
    .data;

export const taslakSil = async (id, kullanici) =>
  (await axios.delete(kullaniciyla(`/deleteSimulationDraft/${id}`, kullanici), { auth }))
    .data;

/* --- Koşu ve yayın ---------------------------------------------------- */

export const calistir = async (govde, kullanici) =>
  (
    await axios.post(kullaniciyla("/runSimulation", kullanici), govde, {
      auth,
      timeout: KOSU_TIMEOUT_MS,
    })
  ).data;

/**
 * Canlı planı değiştirir. Gövde `calistir` ile birebir aynı; publish bayrağını
 * backend ekliyor, istemci göndermiyor.
 */
export const canliyaAl = async (govde, kullanici) =>
  (
    await axios.post(kullaniciyla("/publishSimulation", kullanici), govde, {
      auth,
      timeout: KOSU_TIMEOUT_MS,
    })
  ).data;

/* --- Geçmiş ----------------------------------------------------------- */

export const gecmis = (limit = 50) => al(`/getSimulationResults?limit=${limit}`);
